import { Component, DestroyRef, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';
import { LanguageService, Language } from '../../services/language.service';
import { AuthService } from '../../auth/auth.service';
import { SettingsModalComponent, SettingsData } from '../settings-modal/settings-modal.component';
import { SpacesService } from '../../services/spaces.service';
import { GlobalSettingsService } from '../../services/global-settings.service';
import { SpaceConfigOptionsService } from '../../services/space-config-options.service';

@Component({
    selector: 'app-header',
  imports: [TranslateModule, SettingsModalComponent],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss'
})
export class HeaderComponent {
  @Input() mode: 'app' | 'landing' = 'app';
  @Input() showUserMenu = true;
  isSettingsModalOpen = false;
  isUserMenuOpen = false;
  currentLanguage: Language;
  availableLanguages: Language[];
  currentUserName: string | null = null;
  hasUser = false;
  currentUserAvatarUrl: string | null = null;
  currentCountryCode: string;
  currentCurrencyCode: string;
  settingsForModal: SettingsData;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly languageService: LanguageService,
    private readonly authService: AuthService,
    private readonly spacesService: SpacesService,
    private readonly globalSettingsService: GlobalSettingsService,
    private readonly spaceConfigOptions: SpaceConfigOptionsService
  ) {
    const globalSettings = this.globalSettingsService.get();
    this.currentCountryCode = globalSettings.countryCode;
    this.currentCurrencyCode = globalSettings.currencyCode;
    this.settingsForModal = {
      countryCode: globalSettings.countryCode,
      languageCode: globalSettings.languageCode,
      currencyCode: globalSettings.currencyCode
    };

    this.availableLanguages = this.languageService.availableLanguages;
    this.currentLanguage = this.languageService.getCurrentLanguage();
    
    this.languageService.currentLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(lang => {
      this.currentLanguage = lang;
    });

    this.authService.displayName$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((userName) => {
      this.currentUserName = userName;
    });

    this.authService.hasUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((hasUser) => {
      this.hasUser = hasUser;
    });

    this.authService.avatarUrl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((avatarUrl) => {
      this.currentUserAvatarUrl = avatarUrl;
    });

    this.globalSettingsService.settings$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(settings => {
      this.currentCountryCode = settings.countryCode;
      this.currentCurrencyCode = settings.currencyCode;
      this.settingsForModal = {
        countryCode: settings.countryCode,
        languageCode: settings.languageCode,
        currencyCode: settings.currencyCode
      };
    });
  }

  get globalSettingsForModal(): SettingsData {
    return this.settingsForModal;
  }

  private countryNameFromCode(code: string): string {
    return this.spaceConfigOptions.countries.find(c => c.code === code)?.name ?? 'Germany';
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  openSettings(): void {
    this.isUserMenuOpen = false;
    this.isSettingsModalOpen = true;
  }

  closeSettings(): void {
    this.isSettingsModalOpen = false;
  }

  onSettingsSaved(data: SettingsData): void {
    this.globalSettingsService.save(data);

    this.spacesService.updateActiveSpaceLocaleSettings({
      country: this.countryNameFromCode(data.countryCode),
      languageCode: data.languageCode,
      currencyCode: data.currencyCode
    });

    this.isSettingsModalOpen = false;
  }

  closeUserMenu(): void {
    this.isUserMenuOpen = false;
  }

  login(): void {
    this.authService.login();
  }

  logout(): void {
    this.isUserMenuOpen = false;
    this.authService.logout();
  }
}
